/**
 * ProtectedRoute -- redirects unauthenticated users to /login.
 *
 * Wraps any route that requires authentication.
 * Preserves the intended URL as returnUrl query param so the user
 * is redirected back after login.
 *
 * Shows a spinner while auth state is hydrating from localStorage.
 */

import { Navigate, useLocation, Outlet } from 'react-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { Spinner } from '@/components/ui/Spinner';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, isHydrated } = useAuthStore();
  const location = useLocation();

  // Still loading auth state from localStorage -- show spinner
  if (isLoading || !isHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <Spinner size="lg" />
      </div>
    );
  }

  // Not authenticated -- redirect to login with returnUrl
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  // Authenticated -- render child routes
  return <Outlet />;
}
